<template>
  <div>
    <b-card>
      <b-form-row>
        <h5 class="col-12 text-primary font-weight-bold pb-2">
          Non-Harvest Information
        </h5>
      </b-form-row>
      <p class="font-sm">
        Enter acres per non-harvest activity
      </p>
      <div class="text-primary">
        Herbicide Treatment
        <FontAwesomeIcon
          focusable="true"
          tabindex="0"
          class="mx-2 text-primary align-self-center"
          icon="info-circle"
          id="herbicide-treatment-popover"
        />
        <b-popover
          target="herbicide-treatment-popover"
          :triggers="['hover', 'focus']"
          placement="right"
          custom-class="popover-lg"
        >
          Herbicides are used in forest management for two basic reasons:
          <ol>
            <li>
              To remove "unwanted plants" in preparation for planting, called site
              preparation
            </li>
            <li>To release softwood from competing hardwood.</li>
          </ol>
          Record separately the acres treated to prepare the site for reforestation (site
          preparation) and/or acres of land released from competing vegetation.
        </b-popover>
      </div>
      <ValidationProvider
        name="Site Preparation Acres"
        rules="max_decimal_singular:1|min_value:0"
        v-slot="{ errors, classes }"
        slim
      >
        <b-form-group label-cols="12" label-cols-lg="6" label-for="sitePreperationAcres">
          <template v-slot:label>
            Site Preparation Acres
          </template>
          <b-form-input
            type="number"
            id="sitePreperationAcres"
            name="sitePreperationAcres"
            v-model="nonHarvestActivities.sitePreperationAcres"
            placeholder="Enter Acres"
            :class="classes"
            maxlength="7"
            :aria-describedby="$errorsIdString(errors, 'sitePreperationAcres')"
          />
          <InputErrors :errors="errors" errorsIdPrefix="sitePreperationAcres" />
        </b-form-group>
      </ValidationProvider>
      <ValidationProvider
        name="Release Acres"
        rules="max_decimal_singular:1|min_value:0"
        v-slot="{ errors, classes }"
        slim
      >
        <b-form-group label-cols="12" label-cols-lg="6" label-for="releaseAcres">
          <template v-slot:label>
            Release Acres
          </template>
          <b-form-input
            type="number"
            id="releaseAcres"
            name="releaseAcres"
            v-model="nonHarvestActivities.releaseAcres"
            placeholder="Enter Acres"
            :class="classes"
            maxlength="7"
            :aria-describedby="$errorsIdString(errors, 'releaseAcres')"
          />
          <InputErrors :errors="errors" errorsIdPrefix="releaseAcres" />
        </b-form-group>
      </ValidationProvider>
      <hr class="border-dark" />
      <div class="text-primary">
        Non-commercial Thinning
        <FontAwesomeIcon
          focusable="true"
          tabindex="0"
          class="mx-2 text-primary align-self-center"
          icon="info-circle"
          id="thinning-popover"
        />
        <b-popover
          target="thinning-popover"
          :triggers="['hover', 'focus']"
          placement="right"
          custom-class="popover-lg"
        >
          Pre-commercial thinning or weeding of young stands to improve the growing conditions
          for the residual crop. In a TSI procedure no, or very few, commercial forest products
          are harvested. Record the number of acres thinned or weeded using mechanical means
          (saws, axes, etc. but not herbicides).
        </b-popover>
      </div>
      <ValidationProvider
        name="Non-commercial Thinning Acres"
        rules="max_decimal_singular:1|min_value:0"
        v-slot="{ errors, classes }"
        slim
      >
        <b-form-group label-cols="12" label-cols-lg="6" label-for="nonCommercialThinningAcres">
          <template v-slot:label>
            Non-commercial Thinning Acres
          </template>
          <b-form-input
            type="number"
            id="nonCommercialThinningAcres"
            name="nonCommercialThinningAcres"
            v-model="nonHarvestActivities.nonCommercialThinningAcres"
            placeholder="Enter Acres"
            :class="classes"
            maxlength="7"
            :aria-describedby="$errorsIdString(errors, 'nonCommercialThinningAcres')"
          />
          <InputErrors :errors="errors" errorsIdPrefix="nonCommercialThinningAcres" />
        </b-form-group>
      </ValidationProvider>
      <hr class="border-dark" />
      <div class="text-primary">
        Tree Planting
        <FontAwesomeIcon
          focusable="true"
          tabindex="0"
          class="mx-2 text-primary align-self-center"
          icon="info-circle"
          id="tree-planting-popover"
        />
        <b-popover
          target="tree-planting-popover"
          :triggers="['hover', 'focus']"
          placement="right"
          custom-class="popover-lg"
        >
          Report the species and acres planted for each tree species used in planting
          activities. Planting may be by direct seeding or by planting seedlings.
        </b-popover>
      </div>
      <span v-if="!nonHarvestActivities.treePlantings.length">None</span>
      <template v-for="(treePlanting, index) in nonHarvestActivities.treePlantings">
        <hr :key="`${index}hr`" v-if="index" class="d-lg-none" />
        <b-row :key="index">
          <b-col lg="5">
            <ValidationProvider
              :name="`Tree Species ${index + 1}`"
              rules='required'
              v-slot="{ errors, classes }"
              slim
            >
              <b-form-group
                label-cols="12"
                label-cols-lg="4"
                :label-for="`tree-planting-species-${index + 1}`"
              >
                <template v-slot:label>
                  Tree Species
                </template>
                <select
                  :id="`tree-planting-species-${index + 1}`"
                  :name="`tree-planting-species-${index + 1}`"
                  v-model="treePlanting.treeSpeciesId"
                  class="form-control"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, `tree-planting-species-${index + 1}`)"
                >
                  <option :value="null">Select a Tree Species</option>
                  <option
                    v-for="option in treeSpecies"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{option.name}}
                  </option>
                </select>
                <InputErrors
                  :errors="errors"
                  :errorsIdPrefix="`tree-planting-species-${index + 1}`"
                />
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col lg="6">
            <ValidationProvider
              :name="`Tree Planting Acres ${index + 1}`"
              rules="required|max_decimal_singular:1|min_value:0.1"
              v-slot="{ errors, classes }"
              slim
            >
              <b-form-group
                label-cols="12"
                label-cols-lg="5"
                :label-for="`tree-planting-acres-${index + 1}`"
                label-class="text-lg-right"
              >
                <template v-slot:label>
                  Area of tree planting (acres)
                </template>
                <b-form-input
                  type="number"
                  :id="`tree-planting-acres-${index + 1}`"
                  :name="`tree-planting-acres-${index + 1}`"
                  v-model="treePlanting.acres"
                  placeholder="Enter Acres"
                  maxlength="7"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, `tree-planting-acres-${index + 1}`)"
                />
                <InputErrors :errors="errors" :errorsIdPrefix="`tree-planting-acres-${index + 1}`" />
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <div class="col-12 col-lg-1 text-right">
            <b-button
              class="form-control-height w-100 w-lg-auto"
              variant="danger"
              @click="removeTreePlanting(index)"
              aria-label="Remove tree planting"
            >
              <FontAwesomeIcon class="align-self-center" icon="trash-alt" />
            </b-button>
          </div>
        </b-row>
      </template>
      <div class="text-right">
        <a class="form-action-text text-secondary"
          @click="addTreePlanting"
        >
          + Add Tree Planting Row
        </a>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import LookupModules from '@/store/moduleConfig';

export default {
  name: 'NonHarvestActivitiesForm',
  props: {
    nonHarvestActivities: {
      type: Object,
      required: true,
    },
  },
  created() {
    this.$store.dispatch(`${LookupModules.TREE_SPECIES.namespace}/fetchOptions`);
  },
  computed: {
    ...mapState(LookupModules.TREE_SPECIES.namespace, {
      treeSpecies: state => state.options,
      treeSpeciesLoading: state => state.loading,
    }),
  },
  methods: {
    addTreePlanting() {
      this.nonHarvestActivities.treePlantings.push({ treeSpeciesId: null, acres: null });
    },
    removeTreePlanting(index) {
      this.nonHarvestActivities.treePlantings.splice(index, 1);
    },
  },
};
</script>
